/**
 * «Статистика» — Spec 023, round-4 mockup, approved by Alexander («С макетом норм»).
 *
 * Who did what comes from the activity log and is counted, not reported. What
 * agents spent is their own report and says so; an agent that reported nothing
 * reads «нет данных», never a zero. Three views of the same period: by agent,
 * by task, by project.
 *
 * One person's connections are one agent here. Alexander, 28.09: «для нас агенты
 * наши это по сути отдельные сессии» — so the screen adds up a person's sessions
 * and shows the total; the API keeps the per-connection detail for agents.
 */

import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Stats } from "@/api/resources";
import type { ActionCounts, AgentStat, ByAgent, StatsPerson, StatsPeriod, UsageSum } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { AppBar, ago, blockPanel, BlockTabs, Caption, Chip, EmptyNote, ScreenHeader, whoDid, type BlockTab } from "@/kit";
import { cn } from "@/lib/cn";
import { useBackButton } from "@/lib/telegram-ui";

const VIEWS = ["agents", "tasks", "projects"] as const;
type View = (typeof VIEWS)[number];
const PERIODS: { key: StatsPeriod; label: string }[] = [
  { key: "week", label: "Неделя" },
  { key: "month", label: "Месяц" },
  { key: "all", label: "Всё время" },
];
// Brand tints for the agents' shares; yellow stays the one main control's (D2).
const SHARE = ["bg-p-review", "bg-p-link", "bg-p-done", "bg-p-needs", "bg-p-waiting"];

function plural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

/** «1,2 млн», «820 тыс.», «950». */
export function tokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} млн`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toLocaleString("ru-RU")} тыс.`;
  return String(n);
}

/** «6 ч 40 мин», «25 мин». */
export function duration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? (m ? `${h} ч ${m} мин` : `${h} ч`) : `${m} мин`;
}

/** The spend in words, or «нет данных» when nothing was reported. */
export function spend(u: UsageSum | null): string {
  if (!u) return "нет данных";
  const all = u.tokens_in + u.tokens_out;
  const tokenPart = u.records_without_tokens === u.records ? "токены: нет данных" : `${tokens(all)} токенов`;
  return `${tokenPart} · ${duration(u.minutes)}`;
}

/** The sum of several reports; «нет данных» (null) only when none of them reported. */
export function mergeUsage(list: (UsageSum | null)[]): UsageSum | null {
  const reported = list.filter((u): u is UsageSum => u !== null);
  if (!reported.length) return null;
  return reported.reduce((a, u) => ({
    records: a.records + u.records,
    minutes: a.minutes + u.minutes,
    tokens_in: a.tokens_in + u.tokens_in,
    tokens_out: a.tokens_out + u.tokens_out,
    records_without_tokens: a.records_without_tokens + u.records_without_tokens,
  }));
}

function addCounts(a: ActionCounts, b: ActionCounts): ActionCounts {
  const out = { ...a };
  for (const key of Object.keys(b) as (keyof ActionCounts)[]) out[key] = (a[key] ?? 0) + b[key];
  return out;
}

/** One person's agent: all of their connections added up. */
export interface PersonAgent {
  owner: StatsPerson;
  sessions: number;
  connected: number;
  counts: ActionCounts;
  usage: UsageSum | null;
  last_at: string | null;
}

export function byPerson(agents: AgentStat[]): PersonAgent[] {
  const groups = new Map<string, AgentStat[]>();
  for (const a of agents) groups.set(a.owner.id, [...(groups.get(a.owner.id) ?? []), a]);
  return [...groups.values()]
    .map((list) => ({
      owner: list[0].owner,
      sessions: list.length,
      connected: list.filter((a) => !a.revoked).length,
      counts: list.map((a) => a.counts).reduce(addCounts),
      usage: mergeUsage(list.map((a) => a.usage)),
      last_at: list.reduce<string | null>((last, a) => (a.last_at && (!last || a.last_at > last) ? a.last_at : last), null),
    }))
    .sort((a, b) => b.counts.total - a.counts.total || a.owner.name.localeCompare(b.owner.name, "ru"));
}

/** A row's share of one person's agent. */
export interface PersonShare {
  owner_id: string;
  owner_name: string;
  actions: number;
}

export function sharesByPerson(by: ByAgent[]): PersonShare[] {
  const out = new Map<string, PersonShare>();
  for (const b of by) {
    const key = b.owner_id ?? b.owner_name;
    out.set(key, { owner_id: key, owner_name: b.owner_name, actions: (out.get(key)?.actions ?? 0) + b.actions });
  }
  return [...out.values()].sort((a, b) => b.actions - a.actions || a.owner_name.localeCompare(b.owner_name, "ru"));
}

/** What people did by hand: the row's actions less the agents' ones. */
export const byPeople = (actions: number, by: { actions: number }[]) => Math.max(actions - by.reduce((n, b) => n + b.actions, 0), 0);

type Tint = (ownerId: string) => string;

/** One colour per person's agent for the whole screen, whatever the row or the period. */
function tints(agents: AgentStat[]): Tint {
  const owners = new Map<string, string>();
  for (const a of agents) owners.set(a.owner.id, a.owner.name);
  const order = [...owners]
    .sort(([idA, nameA], [idB, nameB]) => nameA.localeCompare(nameB, "ru") || idA.localeCompare(idB))
    .map(([id]) => id);
  // A share from a server without `owner_id` is keyed by the owner's name: find their id by it.
  const byName = new Map([...owners].map(([id, name]) => [name, id]));
  return (key) => {
    const at = order.indexOf(key);
    return SHARE[Math.max(at >= 0 ? at : order.indexOf(byName.get(key) ?? ""), 0) % SHARE.length];
  };
}

/** The shares of the row's actions: each person's agent in its colour, people by hand in grey, so the bar adds up to the count. */
function Split({ shares, people, tint }: { shares: PersonShare[]; people: number; tint: Tint }) {
  const total = shares.reduce((n, s) => n + s.actions, 0) + people;
  if (!total) return null;
  const width = (n: number) => ({ width: `${(n / total) * 100}%` });
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-p-surface-2" aria-hidden="true">
      {shares.map((s) => <span key={s.owner_id} className={tint(s.owner_id)} style={width(s.actions)} />)}
      {people > 0 && <span className="bg-p-border-strong" style={width(people)} />}
    </div>
  );
}

function Legend({ shares, people, tint }: { shares: PersonShare[]; people: number; tint: Tint }) {
  if (!shares.length && !people) return null;
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1 text-micro text-p-text-muted">
      {shares.map((s) => (
        <span key={s.owner_id} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={cn("h-2 w-2 rounded-sm", tint(s.owner_id))} />
          {whoDid(s.owner_name, true)} {s.actions}
        </span>
      ))}
      {people > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2 w-2 rounded-sm bg-p-border-strong" />
          люди {people}
        </span>
      )}
    </span>
  );
}

function Num({ value, label }: { value: number; label: string }) {
  return (
    <span className="flex min-w-0 flex-col rounded-xl bg-p-surface-2 px-2.5 py-2">
      <span className="text-[20px] font-bold leading-6 text-p-text">{value}</span>
      <span className="text-micro text-p-text-muted">{label}</span>
    </span>
  );
}

/** «3 сессии · подключена 1», «2 сессии», «все сессии отключены». */
export function sessionsLine(p: Pick<PersonAgent, "sessions" | "connected">): string {
  const all = `${p.sessions} ${plural(p.sessions, "сессия", "сессии", "сессий")}`;
  if (p.connected === p.sessions) return all;
  if (!p.connected) return p.sessions === 1 ? "сессия отключена" : `${all}, все отключены`;
  return `${all} · ${plural(p.connected, "подключена", "подключены", "подключено")} ${p.connected}`;
}

function PersonCard({ p, tint }: { p: PersonAgent; tint: Tint }) {
  const c = p.counts;
  return (
    <article className="flex flex-col gap-2.5 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
      <span className="flex items-start gap-2">
        <span aria-hidden="true" className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm", tint(p.owner.id))} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[16px] font-semibold text-p-text">{whoDid(p.owner.name, true)}</span>
          <span className="truncate text-meta text-p-text-muted">{sessionsLine(p)}</span>
        </span>
      </span>
      <span className="grid grid-cols-3 gap-2">
        <Num value={c.total} label={plural(c.total, "действие", "действия", "действий")} />
        <Num value={c.results_prepared} label={plural(c.results_prepared, "результат", "результата", "результатов")} />
        <Num value={c.results_confirmed_first_time} label="с первого раза" />
      </span>
      <span className="flex items-baseline justify-between gap-3 text-body">
        <span className="text-p-text">Расход</span>
        <b className={cn("text-right font-bold", p.usage ? "text-p-text" : "text-p-text-muted")}>{spend(p.usage)}</b>
      </span>
      <span className="text-micro text-p-text-muted">
        {p.usage
          ? `самоотчёт агента: ${p.usage.records} ${plural(p.usage.records, "запись", "записи", "записей")}${p.usage.records_without_tokens ? `, в ${p.usage.records_without_tokens} без токенов` : ""}`
          : "агент ещё не записывал расход"}
        {p.last_at ? ` · последнее действие ${ago(p.last_at)}` : ""}
      </span>
    </article>
  );
}

export function StatsScreen() {
  useBackButton("/knowledge");
  const [params, setParams] = useSearchParams();
  // A hand-edited address falls back to the defaults instead of asking the server for nonsense.
  const period = PERIODS.find((p) => p.key === params.get("p"))?.key ?? "week";
  const view: View = VIEWS.find((v) => v === params.get("v")) ?? "agents";
  const stats = useQuery({ queryKey: ["stats", "agents", period], queryFn: () => Stats.agents(period) });
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };
  const tabs: BlockTab<View>[] = [
    { key: "agents", label: "По агентам" },
    { key: "tasks", label: "По задачам" },
    { key: "projects", label: "По проектам" },
  ];
  const d = stats.data;
  const tint = tints(d?.agents ?? []);
  const persons = byPerson(d?.agents ?? []);

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/knowledge" context="Знания" />
      <ScreenHeader title="Статистика" sub="кто что сделал и сколько потратил" />
      <div className="flex gap-2">
        {PERIODS.map((p) => <Chip key={p.key} on={period === p.key} onClick={() => set("p", p.key)}>{p.label}</Chip>)}
      </div>
      <BlockTabs label="Разрезы статистики" id="stats" tabs={tabs} value={view} onChange={(v) => set("v", v)} />
      <div {...blockPanel("stats", view)} className="flex flex-col gap-3 pt-1">
        {stats.isLoading && <SkeletonList count={3} />}
        {stats.isError && (
          <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void stats.refetch()}>Повторить</Button>}>
            Не удалось загрузить статистику.
          </EmptyNote>
        )}
        {d && view === "agents" && (
          <>
            {persons.length === 0 && <EmptyNote>Подключённых агентов нет.</EmptyNote>}
            {persons.length > 0 && (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">{persons.map((p) => <PersonCard key={p.owner.id} p={p} tint={tint} />)}</div>
            )}
            {persons.length > 0 && (
              <p className="text-micro text-p-text-muted">Все сессии одного человека сложены в его агента. Расход — самоотчёт агентов.</p>
            )}
            {d.people.length > 0 && (
              <>
                <Caption>Люди сами</Caption>
                <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                  {d.people.map((p) => (
                    <li key={p.person.id} className="border-b border-p-border px-4 py-3 last:border-0">
                      <span className="block text-[15px] font-semibold text-p-text">{p.person.name}</span>
                      <span className="block text-meta text-p-text-muted">
                        {p.counts.total} {plural(p.counts.total, "действие", "действия", "действий")}
                        {p.counts.messages ? ` · ${p.counts.messages} ${plural(p.counts.messages, "сообщение", "сообщения", "сообщений")}` : ""}
                        {p.last_at ? ` · ${ago(p.last_at)}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
        {d && view === "tasks" && (
          <>
            {d.tasks.length === 0 && <EmptyNote>За этот период по задачам ничего не записано.</EmptyNote>}
            {d.tasks.length > 0 && (
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                {d.tasks.map((t) => {
                  const shares = sharesByPerson(t.by);
                  const people = byPeople(t.actions, t.by);
                  return (
                    <li key={t.task_id} className="border-b border-p-border last:border-0">
                      <Link to={`/tasks/${t.task_id}`} className="flex flex-col gap-1.5 px-4 py-3 press-surface">
                        <span className="text-[15px] font-semibold leading-5 text-p-text">{t.title}</span>
                        <Split shares={shares} people={people} tint={tint} />
                        <span className="text-meta text-p-text-muted">
                          {t.actions} {plural(t.actions, "действие", "действия", "действий")} · {spend(t.usage)}
                        </span>
                        <Legend shares={shares} people={people} tint={tint} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-micro text-p-text-muted">Полоса — кто сколько сделал в задаче: агенты цветом, люди серым. Токены и минуты — самоотчёт агентов.</p>
          </>
        )}
        {d && view === "projects" && (
          <>
            {d.projects.length === 0 && <EmptyNote>За этот период по проектам ничего не записано.</EmptyNote>}
            {d.projects.map((p) => {
              const shares = sharesByPerson(p.by);
              const people = byPeople(p.actions, p.by);
              return (
                <Link key={p.project_id} to={`/projects/${p.project_id}`} className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3.5 press-surface">
                  <span className="flex items-baseline gap-3">
                    <span className="min-w-0 flex-1 truncate text-[16px] font-semibold text-p-text">{p.title}</span>
                    <span className="text-meta text-p-text-muted">{p.actions} {plural(p.actions, "действие", "действия", "действий")}</span>
                  </span>
                  <Split shares={shares} people={people} tint={tint} />
                  <span className="flex items-baseline justify-between gap-3 text-body">
                    <span className="text-p-text">Расход</span>
                    <b className={cn("text-right font-bold", p.usage ? "text-p-text" : "text-p-text-muted")}>{spend(p.usage)}</b>
                  </span>
                  <Legend shares={shares} people={people} tint={tint} />
                </Link>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
